import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Plus, CheckCircle, Clock, DollarSign, TrendingUp, Calendar, FolderKanban } from "lucide-react";
import Header from "../components/layout/Header";
import { Badge, SearchInput, Table } from "../components/ui/index";
import AddAbonnementModal from "../components/modals/AddAbonnementModal";
import EditAbonnementModal from "../components/modals/EditAbonnementModal";
import ConfirmModal from "../components/modals/ConfirmModal";
import { abonnementsAPI, cascadeAPI } from "../api/services";

const planBadge = {
  "Starter":      "bg-gray-100 text-gray-600 border border-gray-200",
  "Professional": "bg-blue-100 text-blue-700 border border-blue-200",
  "Premium":      "bg-purple-100 text-purple-700 border border-purple-200",
};

export default function Abonnements() {
  const { t } = useTranslation();
  const [search, setSearch]             = useState("");
  const [list, setList]                 = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showAdd, setShowAdd]           = useState(false);
  const [editItem, setEditItem]         = useState(null);
  const [confirmItem, setConfirmItem]   = useState(null);
  const [openMenu, setOpenMenu]         = useState(null);

  const fetchAbonnements = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      const { data } = await abonnementsAPI.getAll(params);
      setList(Array.isArray(data) ? data : data.data ?? []);
    } catch (err) {
      console.error("Erreur chargement abonnements:", err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchAbonnements();
  }, [fetchAbonnements]);

  const actifs    = list.filter(a => a.statut === "Actif").length;
  const attente   = list.filter(a => a.statut === "En attente").length;
  const mrr       = list.filter(a => a.statut === "Actif" && a.cycle === "Mensuel").reduce((s, a) => s + (a.prix || 0), 0);
  const revenus   = list.filter(a => a.statut === "Actif").reduce((s, a) => {
    if (a.cycle === "Mensuel")     return s + (a.prix || 0);
    if (a.cycle === "Trimestriel") return s + (a.prix || 0) / 3;
    if (a.cycle === "Annuel")      return s + (a.prix || 0) / 12;
    return s;
  }, 0);

  const handleAdd = async (data) => {
    try {
      await abonnementsAPI.create(data);
      setShowAdd(false);
      fetchAbonnements();
    } catch (err) {
      console.error("Erreur création abonnement:", err);
    }
  };

  const handleEdit = async (data) => {
    try {
      await abonnementsAPI.update(editItem._id, data);
      setEditItem(null);
      fetchAbonnements();
    } catch (err) {
      console.error("Erreur modification abonnement:", err);
    }
  };

  const handleCancel = async () => {
    try {
      await abonnementsAPI.remove(confirmItem._id);
      setConfirmItem(null);
      fetchAbonnements();
    } catch (err) {
      console.error("Erreur suppression abonnement:", err);
    }
  };

  return (
    <>
      <Header title={t('dashboard.title')} subtitle="" />

      {/* Titre section + bouton */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-slate-800">{t('subscriptions.title')}</h2>
          <p className="text-sm text-slate-400">{list.length} {t('subscriptions.totalCount')}</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors"
        >
          <Plus size={16} /> {t('subscriptions.addSubscription')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        {[
          { icon: CheckCircle, iconBg: "bg-green-100", iconColor: "text-green-600", label: t('subscriptions.activeSubscriptions'), value: actifs },
          { icon: Clock,       iconBg: "bg-yellow-100", iconColor: "text-yellow-500", label: t('subscriptions.pending'),        value: attente },
          { icon: DollarSign,  iconBg: "bg-blue-100",  iconColor: "text-blue-600",  label: t('subscriptions.mrr'),               value: `${mrr.toLocaleString()}€` },
          { icon: TrendingUp,  iconBg: "bg-purple-100",iconColor: "text-purple-600",label: t('subscriptions.activeRevenue'),    value: `${Math.round(revenus * 12).toLocaleString()}€` },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center gap-4">
            <div className={`w-12 h-12 ${s.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
              <s.icon size={22} className={s.iconColor} />
            </div>
            <div>
              <p className="text-xs text-slate-400">{s.label}</p>
              <p className="text-2xl font-bold text-slate-800">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Recherche */}
      <div className="mb-4">
        <SearchInput value={search} onChange={setSearch} placeholder="Rechercher par projet, client, revendeur, plan, statut..." />
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400 text-sm">{t('common.loading')}</div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                {[t('subscriptions.colProject'), t('subscriptions.colClient'), t('subscriptions.colPlan'), t('subscriptions.colPrice'), t('subscriptions.colCycle'), t('subscriptions.colNextPayment'), t('subscriptions.colStatus'), t('subscriptions.colActions')].map(h => (
                  <th key={h} className="px-6 py-4 text-left text-xs font-semibold text-slate-400 tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map(a => (
                <tr key={a._id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  {/* Projet */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <FolderKanban size={18} className="text-purple-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">{a.projet?.nom ?? a.projet}</p>
                        <p className="text-xs text-slate-400">{a.sousProjet?.nom ?? a.sousProjet}</p>
                      </div>
                    </div>
                  </td>
                  {/* Client */}
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-slate-800">{a.client?.nom ?? a.client}</p>
                    <p className="text-xs text-slate-400">{a.revendeur?.nom ?? a.revendeur}</p>
                  </td>
                  {/* Plan */}
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${planBadge[a.plan] || "bg-gray-100 text-gray-600"}`}>{a.plan}</span>
                  </td>
                  {/* Prix */}
                  <td className="px-6 py-4">
                    <p className="text-sm font-semibold text-slate-800">{a.prix}€</p>
                    <p className="text-xs text-slate-400">/{a.cycle === "Mensuel" ? t('subscriptions.monthly') : a.cycle === "Trimestriel" ? t('subscriptions.quarterly') : t('subscriptions.annual')}</p>
                  </td>
                  {/* Cycle */}
                  <td className="px-6 py-4 text-sm text-slate-700">{a.cycle}</td>
                  {/* Prochain paiement */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                      <Calendar size={13} className="text-slate-400" /> {a.prochainPaiement}
                    </div>
                  </td>
                  {/* Statut */}
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      a.statut === "Actif" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                    }`}>{a.statut}</span>
                  </td>
                  {/* Actions */}
                  <td className="px-6 py-4 relative">
                    <button
                      onClick={() => setOpenMenu(openMenu === a._id ? null : a._id)}
                      className="p-2 hover:bg-gray-100 rounded-lg text-slate-400"
                    >⋮</button>
                    {openMenu === a._id && (
                      <div className="absolute right-8 top-8 z-20 bg-white border border-gray-100 rounded-xl shadow-lg py-1 min-w-[130px]">
                        <button
                          onClick={() => { setEditItem(a); setOpenMenu(null); }}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-gray-50"
                        >✏️ {t('subscriptions.edit')}</button>
                        <button
                          onClick={() => { setConfirmItem(a); setOpenMenu(null); }}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                        >🗑 {t('subscriptions.cancel')}</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Click outside menu */}
      {openMenu && <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />}

      <AddAbonnementModal  isOpen={showAdd}      onClose={() => setShowAdd(false)}    onAdd={handleAdd} />
      <EditAbonnementModal isOpen={!!editItem}    onClose={() => setEditItem(null)}    item={editItem}    onSave={handleEdit} />
      <ConfirmModal        isOpen={!!confirmItem} onClose={() => setConfirmItem(null)} item={confirmItem} onConfirm={handleCancel} />
    </>
  );
}
